// Vendor Libs
import {
    chain,
    cloneDeep,
    get,
    find,
    findIndex,
    first,
    includes,
    intersection,
    isArray,
    isEmpty,
    isPlainObject,
    isString,
    join,
    map,
    omit,
    pick,
    set,
    uniq,
} from 'lodash';
import moment from 'moment';
import {push} from 'react-router-redux';

// Lib App
import actionsServiceCaller, {generateCancelToken} from './actions-service-caller';

// Configs
import servicesConfig from '../../config/services-config';

const currentCancelTokens = {};

const fetchMiddleware = store => next => action => {
    const {dispatch, getState} = store;
    const state = getState();
    next(action);
    const nextState = getState();
    const actions = {
        GLOBAL_SHELVES_TREE: () => getShelvesTree(dispatch, nextState),
        SEARCH_DATA_REGISTER_STORE: () => storeRegistered(dispatch, getState, action.servicesDataKey, nextState),
        SEARCH_DATA_ROUTER_CHANGED: () => routerChanged(dispatch, action.routerParams, nextState),
        SEARCH_DATA_REFRESH: () =>
            refreshServicesData(dispatch, action.servicesDataKey, nextState, action.selectRecord),
        SEARCH_DATA_SEARCH: () => fetchServicesData(dispatch, action.servicesDataKey, nextState, action.selectRecord),
        SEARCH_DATA_SET_QUERY: () => fetchServicesData(dispatch, action.servicesDataKey, nextState),
        SEARCH_DATA_SORT: () => fetchServicesData(dispatch, action.servicesDataKey, nextState),
        SEARCH_DATA_FETCH_FULFILLED: () => {
            if (action.selectRecord) {
                selectRecord(dispatch, action.servicesDataKey, state, nextState);
            }
            fetchDependentData(dispatch, action.servicesDataKey, nextState);
        },
        SEARCH_DATA_RESET: () => {
            if (action.actionShouldFetch) {
                fetchServicesData(dispatch, action.servicesDataKey, nextState);
            }
        },
        SEARCH_DATA_SORT_RESET: () => {
            if (action.actionShouldFetch) {
                fetchServicesData(dispatch, action.servicesDataKey, nextState);
            }
        },
        FETCH_NEXT_ITEM: () => {
            selectRecord(dispatch, action.servicesDataKey, state, nextState);
        },
        FETCH_PREVIOUS_ITEM: () => {
            selectRecord(dispatch, action.servicesDataKey, state, nextState, true);
        },
    };

    if (actions[action.type]) {
        return actions[action.type]();
    }
};

function selectRecord(dispatch, servicesDataKey, state, nextState, reverse) {
    const {activeRowKeys, route} = servicesConfig[servicesDataKey] || {};

    if (activeRowKeys && route) {
        const searchData = get(state.servicesSearchData, `${servicesDataKey}.searchData.content`, []);
        const nextSearchData = get(nextState.servicesSearchData, `${servicesDataKey}.searchData.content`, []);

        if (searchData.length || nextSearchData.length) {
            const routerParams = get(nextState.servicesSearchData, 'routerParams', {});
            let routeItem;

            if (!isEmpty(routerParams)) {
                const currentIndex = findIndex(searchData, pick(routerParams, activeRowKeys));

                if (currentIndex > -1) {
                    const nextIndex = reverse ? currentIndex - 1 : currentIndex + 1;
                    const nextItem =
                        nextIndex < searchData.length && nextIndex >= 0
                            ? searchData[nextIndex]
                            : nextSearchData[currentIndex];

                    if (nextItem) {
                        routeItem = find(nextSearchData, pick(nextItem, activeRowKeys));
                    }
                }
            }

            routeItem = routeItem || first(nextSearchData);

            if (routeItem) {
                dispatch(
                    push(
                        `/${route}/${join(
                            map(activeRowKeys, rowKey => get(routeItem, rowKey, '')),
                            '/'
                        )}`
                    )
                );
            }
        }
    }
}

function storeRegistered(dispatch, getState, servicesDataKey, nextState) {
    const {dependentServicesDataKey, noFetchOnRegister, formName} = servicesConfig[servicesDataKey] || {};
    let result;

    if (formName) {
        result = new Promise(resolve => {
            Promise.resolve(dispatch({type: 'FORM_REGISTER', formName})).then(() => {
                if (!noFetchOnRegister) {
                    fetchServicesData(dispatch, servicesDataKey, getState()).then(resolve);
                } else {
                    resolve();
                }
            });
        });
    } else if (dependentServicesDataKey) {
        if (nextState.servicesSearchData.dependentServicesDataKey) {
            result = fetchServicesData(dispatch, servicesDataKey, nextState);
        } else {
            result = Promise.resolve(
                dispatch({type: 'SEARCH_DATA_REGISTER_STORE', servicesDataKey: dependentServicesDataKey})
            );
        }
    } else {
        result = fetchServicesData(dispatch, servicesDataKey, nextState);
    }

    return result;
}

function fetchDependentData(dispatch, servicesDataKey, nextState) {
    const {updateDependentKeys} = servicesConfig[servicesDataKey];

    if (updateDependentKeys) {
        map(updateDependentKeys, updateDependentKey => {
            if (nextState.servicesSearchData[updateDependentKey]) {
                const {dependentSearchKeys, dependentServicesDataKey} = servicesConfig[updateDependentKey];
                const searchQuery = {query: {rsql: {}}};

                if (!isEmpty(dependentSearchKeys) && dependentServicesDataKey) {
                    map(dependentSearchKeys, ({queryKey, recordKey, searchType}) => {
                        const recordValues = getDependentRecordValues(recordKey, dependentServicesDataKey, nextState);

                        if (searchType === 'path') {
                            searchQuery.path = searchQuery.path
                                ? [searchQuery.path, recordValues].join('/')
                                : recordValues;
                        } else if (searchType === 'rsqlQuery') {
                            searchQuery.query.rsql[queryKey] = recordValues || -1;
                        }
                    });

                    fetchData(dispatch, updateDependentKey, searchQuery);
                }
            }
        });
    }
}

function getDependentRecordValues(dependentSearchKey, dependentServicesDataKey, nextState) {
    // For now, just working on or statements if there's an ||.
    const searchKeys = includes(dependentSearchKey, '||') ? dependentSearchKey.split('||') : [dependentSearchKey];
    const dependentRecord = get(
        nextState.servicesSearchData,
        [dependentServicesDataKey, 'searchData', 'content', 0],
        {}
    );

    return chain(searchKeys)
        .map(searchKey => get(dependentRecord, searchKey))
        .compact()
        .join('||')
        .value();
}

function routerChanged(dispatch, routerParams, nextState) {
    const stateRouterParams = get(nextState, 'servicesSearchData.routerParams');

    if (stateRouterParams && routerParams) {
        const changedRouterKeys = chain(routerParams)
            .mapValues((value, key) => stateRouterParams[key] !== value)
            .keys()
            .value();
        const servicesDataKeys = Object.keys(nextState.servicesSearchData);

        map(servicesDataKeys, servicesDataKey => {
            const configRouterKeys = map(get(servicesConfig[servicesDataKey], 'routerKeys', []), 'routerKey');

            if (intersection(configRouterKeys, changedRouterKeys).length) {
                fetchServicesData(dispatch, servicesDataKey, nextState);
            }
        });
    }
}

function refreshServicesData(dispatch, servicesDataKey, nextState, selectRecord) {
    const dependentServicesDataKey = get(servicesConfig, [servicesDataKey, 'dependentServicesDataKey']);

    return fetchServicesData(dispatch, dependentServicesDataKey || servicesDataKey, nextState, selectRecord);
}

function getStoreQuery(servicesDataKey, {formData, _router, servicesSearchData}) {
    const {defaultSort, formName, routerKeys, rsqlQuery} = servicesConfig[servicesDataKey] || {};

    return getSearchParams({
        defaultSort,
        formData: formName ? formData[formName] : {},
        routerParams: getRouterParams(routerKeys, get(servicesSearchData, 'routerParams', {})),
        rsqlQuery,
        servicesSearchQuery: get(servicesSearchData, [servicesDataKey, 'query'], {}),
    });
}

// TODO: this needs some cleanup probably.
function getQueryParams(
    {defaultSort, exactSearch, operators, page, pageLimit, _queryParams, rsql, sort, timeout},
    config
) {
    const rsqlQuery = rsqlGenerator(rsql, operators);
    const sortQuery = sortGenerator(sort, defaultSort);
    const query = {
        page: page > 1 ? page - 1 : undefined,
        size: pageLimit !== 20 ? pageLimit : undefined,
        timeout,
    };

    if (exactSearch) {
        query.exactSearch = true;
    }

    if (isArray(sortQuery) && sortQuery.length) {
        set(query, 'sort', sortQuery);
    }

    if (rsqlQuery && config.queryType === 'queryParams') {
        Object.assign(query, rsql);
    } else if (rsqlQuery) {
        set(query, 'q', rsqlQuery);
    }

    return query;
}

function getShelvesTree(dispatch, nextState) {
    const servicesDataKey = 'loadHomeShelf';
    const {path, query} = getStoreQuery(servicesDataKey, nextState);
    return new Promise(resolve => {
        const dispatchFetched = jsonResponse => {
            Promise.resolve(
                dispatch({
                    type: 'GLOBAL_SHELVES_TREE_FETCH_FULFILLED',
                    shelvesTreeData: jsonResponse,
                })
            ).then(resolve);
        };
        const dispatchError = error => {
            Promise.resolve(
                dispatch({
                    type: 'GLOBAL_SHELVES_TREE_FETCH_REJECTED',
                    error,
                })
            ).then(resolve);
        };

        const config = servicesConfig[servicesDataKey];

        const actionParams = {
            cancelToken: getCancelToken(servicesDataKey),
            dispatchFetched,
            dispatchError,
            servicesConfig: config,
        };

        dispatch({type: 'GLOBAL_SHELVES_TREE_FETCH', servicesDataKey});

        actionsServiceCaller(actionParams, getQueryParams(query, config), path);
    });
}

function fetchServicesData(dispatch, servicesDataKey, nextState, selectRecord) {
    const storeQuery = getStoreQuery(servicesDataKey, nextState);

    return fetchData(
        dispatch,
        servicesDataKey,
        storeQuery,
        get(nextState, 'servicesSearchData.routerParams'),
        selectRecord
    );
}

function shouldFetchData(servicesDataKey, path, query) {
    const {routerKeys} = servicesConfig[servicesDataKey];
    let result;

    if (!routerKeys) {
        result = true;
    } else {
        map(routerKeys, ({queryKey, routerKey, searchType}) => {
            result =
                result ||
                (searchType === 'path' && !isEmpty(path)) ||
                (searchType === 'rsqlQuery' && query.rsql[queryKey || routerKey]);
        });
    }

    return result;
}

function getCancelToken(servicesDataKey) {
    if (currentCancelTokens[servicesDataKey]) {
        currentCancelTokens[servicesDataKey].cancel();
    }

    const newToken = (currentCancelTokens[servicesDataKey] = generateCancelToken());

    return newToken.token;
}

function fetchData(dispatch, servicesDataKey, {path, query}, routerParams, selectRecord = false) {
    return new Promise(resolve => {
        if (shouldFetchData(servicesDataKey, path, query)) {
            const dispatchFetched = jsonResponse => {
                Promise.resolve(
                    dispatch({
                        type: 'SEARCH_DATA_FETCH_FULFILLED',
                        path,
                        query,
                        searchData: jsonResponse,
                        servicesDataKey,
                        selectRecord: !selectRecord ? isEmpty(routerParams) : selectRecord,
                    })
                ).then(resolve);
            };
            const dispatchError = error => {
                Promise.resolve(
                    dispatch({
                        type: 'SEARCH_DATA_FETCH_REJECTED',
                        error,
                        servicesDataKey,
                    })
                ).then(resolve);
            };
            const dispatchCancel = () => {
                Promise.resolve(
                    dispatch({
                        type: 'SEARCH_DATA_FETCH_CANCEL',
                        servicesDataKey,
                    })
                ).then(resolve);
            };
            const config = servicesConfig[servicesDataKey];
            const actionParams = {
                cancelToken: getCancelToken(servicesDataKey),
                dispatchCancel,
                dispatchFetched,
                dispatchError,
                servicesConfig: config,
            };

            dispatch({type: 'SEARCH_DATA_FETCH', servicesDataKey});
            actionsServiceCaller(actionParams, getQueryParams(query, config), path);
        } else {
            Promise.resolve(dispatch({type: 'SEARCH_DATA_RESET', servicesDataKey})).then(resolve);
        }
    });
}

function getSearchParams({defaultSort, formData, rsqlQuery, routerParams, servicesSearchQuery}) {
    const rsql = {
        ...formData.values,
        ...routerParams.rsqlQuery,
        ...rsqlQuery,
    };

    return {
        path: routerParams.path,
        query: {
            ...cloneDeep(servicesSearchQuery),
            defaultSort,
            exactSearch: !!rsql.exactSearch,
            rsql: omit(rsql, 'exactSearch'),
            operators: formData.operators,
        },
    };
}

function getRouterParams(routerKeys, params) {
    const routerParams = {rsqlQuery: {}};

    map(routerKeys, routerConfig => {
        const {defaultValue, operator, queryKey, routerKey, searchType} = routerConfig;
        const routerValue = params[routerKey] || defaultValue;

        if (routerValue) {
            if (searchType === 'path') {
                routerParams.path = routerParams.path ? [routerParams.path].join(routerValue, '/') : routerValue;
            } else if (searchType === 'rsqlQuery') {
                const valueOperator = operator || '';

                routerParams.rsqlQuery[queryKey || routerKey] = valueOperator + routerValue;
            }
        }
    });

    return routerParams;
}

function rsqlGenerator(rsql, operators) {
    let rsqlQuery = [];

    if (rsql) {
        rsqlQuery = plainObjectFlattener({rsql, operators});
    }

    return rsqlQuery.join(';');
}

function escapeRsqlValue(value) {
    return isString(value) ? value.replace(/["]/g, '\\"') : value;
}

function getOperatorGroup(value, keyPath, operator) {
    let argument;

    if (isArray(value)) {
        argument = value.length ? `("${map(value, rsqlValue => escapeRsqlValue(rsqlValue)).join('","')}")` : undefined;
    } else {
        argument = escapeRsqlValue(value);
    }

    return argument ? `${keyPath}${operator}${argument}` : undefined;
}

function getCriteriaGroup(value, keyPath) {
    let comparisons;
    let rsql;

    if (includes(keyPath, '&&')) {
        comparisons = uniq(map(keyPath.split('&&'), key => getCriteria(value, key)));
        rsql = comparisons.join(';');
    } else {
        comparisons = uniq(map(keyPath.split('||'), key => getCriteria(value, key)));
        rsql = comparisons.join(',');
        rsql = comparisons.length > 1 ? `(${rsql})` : rsql;
    }

    return rsql;
}

/**
 * Classifies a raw string value into an RSQL operator and argument pair.
 * Uses regex patterns to detect intent encoded in the value prefix.
 * Priority order: null checks, negation, comparison operators,
 * set membership (in/out), raw RSQL passthrough, between ranges,
 * then defaults to equality. This lets form inputs express complex
 * query semantics through simple string conventions.
 *
 * The keyPath may contain a '#' separator marking metadata-only keys.
 * Everything after '#' is stripped so those segments never appear in
 * the final RSQL output sent to the API. Only the real field path
 * before '#' becomes the query key.
 */
function getCriteria(unescapedValue, keyPath) {
    const value = escapeRsqlValue(unescapedValue);
    let operator;
    let argument;

    if (value === 'isNull') {
        operator = '=isNull=';
        argument = 'true';
    } else if (value === 'notNull') {
        operator = '=notNull=';
        argument = 'true';
    } else if (/^!/.test(value)) {
        operator = '!=';
        argument = `"${value.substr(1)}"`;
    } else if (/^>=/.test(value)) {
        operator = '=ge=';
        argument = `"${value.substr(2)}"`;
    } else if (/^>/.test(value)) {
        operator = '=gt=';
        argument = `"${value.substr(1)}"`;
    } else if (/^<=/.test(value)) {
        operator = '=le=';
        argument = `"${value.substr(2)}"`;
    } else if (/^</.test(value)) {
        operator = '=lt=';
        argument = `"${value.substr(1)}"`;
    } else if (includes(value, '||')) {
        operator = '=in=';
        argument = `("${value.split('||').join('","')}")`;
    } else if (/^=(not_in|out)=.+/.test(value)) {
        operator = '=out=';
        argument = unescapedValue.replace(/^=(not_in|out)=/, '');
    } else if (/^=rsql=.+/.test(value)) {
        keyPath = '';
        operator = '';
        argument = unescapedValue.substr(6);
    } else if (/^=between=\(.+\)$/.test(value)) {
        operator = '=between=';
        argument = value.substr(9);
    } else {
        operator = '==';
        argument = `"${value}"`;
    }

    return `${first(keyPath.split('#'))}${operator}${argument}`;
}

// TODO: eventually refactor this to use operators from the store only.
/**
 * Recursively walks a nested form-data object, building an array of RSQL
 * query segments that the API understands as filter criteria.
 *
 * Date ranges get special treatment: "dateRange" keys are converted into
 * RSQL between, gt, or le operators with normalized timestamps so the
 * backend receives precise date boundaries rather than raw form values.
 *
 * Two branching paths exist at leaf values:
 * - When an explicit operator is passed from form config, it is used
 *   directly (operator-based path via getOperatorGroup).
 * - Otherwise the value itself is inspected for pattern prefixes that
 *   imply comparison semantics (criteria-based path via getCriteriaGroup).
 * This split lets configured forms override inferred behavior.
 */
function plainObjectFlattener({keyPath, operators, query, rsql, value}) {
    const currentValue = value === false || value ? value : rsql;
    const currentQuery = query || [];

    if (isPlainObject(currentValue)) {
        Object.keys(currentValue).forEach(key => {
            let newKeypath = keyPath ? `${keyPath}.${key}` : key;

            if (key === 'dateRange') {
                let dateFrom = get(currentValue, 'dateRange.from');
                let dateTo = get(currentValue, 'dateRange.to');

                if (dateFrom) {
                    dateFrom = moment(dateFrom.includes('T') ? dateFrom : `${dateFrom} 00:00:00`).format();
                }

                if (dateTo) {
                    dateTo = moment(dateTo.includes('T') ? dateTo : `${dateTo} 23:59:59`).format();
                }

                newKeypath = newKeypath.split('.dateRange').join('');

                if (dateFrom && dateTo) {
                    plainObjectFlattener({
                        value: `=between=(${dateFrom},${dateTo})`,
                        keyPath: newKeypath,
                        query: currentQuery,
                    });
                } else if (dateFrom) {
                    plainObjectFlattener({value: `>${dateFrom}`, keyPath: newKeypath, query: currentQuery});
                } else if (dateTo) {
                    plainObjectFlattener({value: `<=${dateTo}`, keyPath: newKeypath, query: currentQuery});
                }
            } else {
                plainObjectFlattener({
                    value: currentValue[key],
                    keyPath: newKeypath,
                    query: currentQuery,
                    operators: get(operators, key),
                });
            }
        });
    } else if ((currentValue || currentValue === false) && currentValue !== '') {
        if (operators) {
            const operatiorGroup = getOperatorGroup(currentValue, keyPath, operators);

            if (operatiorGroup) {
                currentQuery.push(operatiorGroup);
            }
        } else if (!/^#/.test(keyPath)) {
            currentQuery.push(getCriteriaGroup(currentValue, keyPath));
        }
    }

    return currentQuery;
}

function sortGenerator(sort, defaultSort) {
    const sortQuery = map(sort, ({column, order}) => [column, order].join(','));
    const addDefaultSort = defaultSort => {
        if (defaultSort && defaultSort.length === 2 && !find(sort, ['column', defaultSort[0]])) {
            sortQuery.push(defaultSort.join(','));
        }
    };

    if (defaultSort && defaultSort.length) {
        addDefaultSort(['_score', 'desc']);

        if (isArray(defaultSort[0])) {
            map(defaultSort, addDefaultSort);
        } else {
            addDefaultSort(defaultSort);
        }
    }

    return sortQuery;
}

export default fetchMiddleware;
